export interface NamedAthleteRecord {
  id?: string;
  athleteId?: string;
  firstName?: string;
  lastName?: string;
  dateOfBirth?: string;
  club?: string;
  nationality?: string;
}

export function normalizedAthleteName(firstName?: string, lastName?: string): string {
  const normalize = (value?: string) =>
    (value ?? "").normalize("NFKC").trim().replace(/\s+/g, " ").toLocaleLowerCase();
  const first = normalize(firstName);
  const last = normalize(lastName);
  return first && last ? `${first}\u0000${last}` : "";
}

/** Returns record keys belonging to groups with the same first AND last name. */
export function findSameNameAthleteKeys<T extends NamedAthleteRecord>(athletes: T[]): Set<string> {
  const groups = new Map<string, string[]>();
  athletes.forEach((athlete, index) => {
    const name = normalizedAthleteName(athlete.firstName, athlete.lastName);
    if (!name) return;
    const key = athlete.id || athlete.athleteId || `index:${index}`;
    groups.set(name, [...(groups.get(name) || []), key]);
  });
  const matches = new Set<string>();
  groups.forEach((keys) => {
    if (keys.length > 1) keys.forEach((key) => matches.add(key));
  });
  return matches;
}

export interface PossibleAthleteMatch<T extends NamedAthleteRecord> {
  athlete: T;
  strength: "strong" | "same_name";
  reasons: Array<"same_name" | "same_dob" | "same_federation_id">;
}

/**
 * Finds records that deserve human review before a new athlete is created.
 * A name match is never treated as proof that two people are the same. DOB or
 * federation-ID agreement upgrades the warning to `strong`, but creation can
 * still continue through an explicit override for legitimate namesakes.
 */
export function findPossibleAthleteMatches<T extends NamedAthleteRecord>(
  athletes: T[],
  candidate: NamedAthleteRecord
): PossibleAthleteMatch<T>[] {
  const candidateName = normalizedAthleteName(candidate.firstName, candidate.lastName);
  const candidateId = String(candidate.athleteId || "").trim().toLocaleLowerCase();
  const candidateDob = String(candidate.dateOfBirth || "").trim();

  return athletes.flatMap((athlete) => {
    const sameName = !!candidateName && normalizedAthleteName(athlete.firstName, athlete.lastName) === candidateName;
    const sameId = !!candidateId && String(athlete.athleteId || "").trim().toLocaleLowerCase() === candidateId;
    if (!sameName && !sameId) return [];

    const sameDob = !!candidateDob && String(athlete.dateOfBirth || "").trim() === candidateDob;
    const reasons: PossibleAthleteMatch<T>["reasons"] = [];
    if (sameName) reasons.push("same_name");
    if (sameDob) reasons.push("same_dob");
    if (sameId) reasons.push("same_federation_id");
    return [{ athlete, strength: sameId || (sameName && sameDob) ? "strong" : "same_name", reasons }];
  });
}
